import React, {createContext, useContext, useReducer} from "react";
import {DocumentProvider} from "./documentContext";

const BatchEditStateContext = createContext(undefined);
const BatchEditDispatchContext = createContext(undefined);

export const STEP_FORWARD = "batchEditContext/stepForward";
export const SELECT_FIELDS = "batchEditContext/selectFields";
export const UNSELECT_FIELDS = "batchEditContext/unselectFields";
export const SELECT_ALL_FIELDS = "batchEditContext/selectAllFields";
export const UNSELECT_ALL_FIELDS = "batchEditContext/unselectAllFields";
export const SET_BATCH_FIELD_FN = "batchEditContext/setBatchFieldFn";

const initialState = {
    availableFields: [
        {name: "title", fn: 'merge'},
        {name: "description", fn: 'merge'},
        {name: "productionDate", fn: 'overwrite'},
        {name: "producer", fn: 'merge'},
        {name: "persons", fn: 'merge'},
        {name: "places", fn: 'merge'},
        {name: "subjects", fn: 'merge'},
        {name: "remarks", fn: 'merge'},
        {name: "languages", fn: 'overwrite'},
        {name: "relations", fn: 'merge'},
        {name: 'referenceTypes', fn: undefined},
        {name: "copyrightAndLicensing", fn: undefined},
    ],
    selectedFields: [],
    activeStepIndex: 0,
};

/**
 * Returns a new state where the batch edit function for the given key is set to the given value.
 *
 * @param {Object} state - The current state.
 * @param {Object} data - An object containing the key and value to update.
 *  - `key`: The name of the field to update.
 *  - `value`: The new value to set for the given key.
 * @return {Object} The new state.
 */
const setBatchFieldFn = (state, data) => {
    const {key, value} = data;
    const fields = [...state.selectedFields];
    const ix = fields.findIndex(f => f.name === key);
    if (ix === -1) {
        return state;
    }
    fields[ix].fn = value;
    return {
        ...state,
        selectedFields: fields
    };
};

const batchEditReducer = (state, action) => {
    switch (action.type) {
        case STEP_FORWARD:
            return {
                ...state,
                activeStepIndex: 1 + state.activeStepIndex,
            };
        case SELECT_FIELDS:
            return {
                ...state,
                selectedFields: [...state.selectedFields, ...action.fields],
            };
        case SELECT_ALL_FIELDS:
            return {
                ...state,
                selectedFields: state.availableFields,
            };
        case UNSELECT_FIELDS:
            return {
                ...state,
                selectedFields: state.selectedFields.filter(
                    (selectedField) => !action.fields.includes(selectedField)
                ),
            };
        case UNSELECT_ALL_FIELDS:
            return {
                ...state,
                selectedFields: [],
            };
        case SET_BATCH_FIELD_FN:
            return setBatchFieldFn(state, action.data);
        default:
            throw new Error(`Unhandled action type: ${action.type}`);
    }
};

export const BatchEditProvider = ({children}) => {
    const [state, dispatch] = useReducer(
        batchEditReducer,
        initialState,
        undefined
    );

    return (
        <DocumentProvider>
            <BatchEditStateContext.Provider value={state}>
                <BatchEditDispatchContext.Provider value={dispatch}>
                    {children}
                </BatchEditDispatchContext.Provider>
            </BatchEditStateContext.Provider>
        </DocumentProvider>
    );
};

export const useBatchEditState = () => {
    const context = useContext(BatchEditStateContext);
    if (undefined === context) {
        throw new Error(
            "useBatchEditState must be used within an BatchEditProvider."
        );
    } else {
        return context;
    }
};

export const useBatchEditDispatch = () => {
    const context = useContext(BatchEditDispatchContext);
    if (undefined === context) {
        throw new Error(
            "useBatchEditDispatch must be used within an BatchEditProvider."
        );
    } else {
        return context;
    }
};
